(() => {
  'use strict';
  const drawer = document.querySelector('[data-ynd-account-drawer]');
  if (!drawer || drawer.dataset.accountDrawerReady || typeof drawer.showModal !== 'function' || typeof drawer.close !== 'function') return;
  drawer.dataset.accountDrawerReady = 'true';
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const triggers = () => document.querySelectorAll('[data-open-account-drawer]');
  let opener;
  let closeTimer;
  let restoreFocus = true;
  const notify = () => document.dispatchEvent(new Event('ynd:overlay'));
  const focus = element => element?.focus({ preventScroll: true });
  const summaryUrl = drawer.dataset.accountSummaryUrl;
  const summaryEnabled = summaryUrl === 'https://my.yndsuits.com/account/session-summary';
  const states = [...drawer.querySelectorAll('[data-account-state]')];
  const heading = drawer.querySelector('#ynd-account-drawer-title');
  const emailNode = drawer.querySelector('[data-account-email]');
  let summaryController;
  let summaryGeneration = 0;
  let summaryTimer;
  const clearIdentity = () => {
    [emailNode].forEach(node => { if (node) { node.textContent = ''; node.hidden = true; } });
  };
  const showState = state => {
    if (!summaryEnabled) return;
    const active = document.activeElement;
    states.forEach(node => { node.hidden = node.dataset.accountState !== state; });
    heading.textContent = state === 'guest' ? 'SIGN IN' : state === 'profile' ? 'MY ACCOUNT' : 'ACCOUNT';
    drawer.setAttribute('aria-busy', state === 'loading' ? 'true' : 'false');
    if (drawer.open && active?.closest('[data-account-state]')?.hidden) focus(drawer.querySelector('[data-close-account-drawer]'));
  };
  const cancelSummary = () => {
    summaryGeneration++;
    summaryController?.abort();
    summaryController = undefined;
    window.clearTimeout(summaryTimer);
    clearIdentity();
    showState('loading');
  };
  const refreshSummary = async () => {
    if (!summaryEnabled || !drawer.open || document.visibilityState === 'hidden') return;
    cancelSummary();
    const generation = summaryGeneration;
    const controller = new AbortController();
    summaryController = controller;
    summaryTimer = window.setTimeout(() => controller.abort(), 8000);
    try {
      const response = await fetch(summaryUrl, {credentials: 'include', cache: 'no-store', redirect: 'error', signal: controller.signal});
      if (!response.ok) throw new Error('Account status unavailable');
      const data = await response.json();
      if (generation !== summaryGeneration || !drawer.open || document.visibilityState === 'hidden') return;
      if (data?.loggedIn === false) { showState('guest'); return; }
      if (data?.loggedIn !== true || (data.email !== null && typeof data.email !== 'string') || (typeof data.email === 'string' && data.email.length > 320)) throw new Error('Invalid account status');
      if (data.email) { emailNode.textContent = data.email; emailNode.hidden = false; }
      showState('profile');
    } catch {
      if (generation === summaryGeneration && drawer.open && document.visibilityState !== 'hidden') { clearIdentity(); showState('error'); }
    } finally {
      if (generation === summaryGeneration) { window.clearTimeout(summaryTimer); summaryController = undefined; }
    }
  };
  if (summaryEnabled) {
    showState('loading');
    drawer.querySelector('[data-account-retry]')?.addEventListener('click', refreshSummary);
    window.addEventListener('blur', () => { if (drawer.open) cancelSummary(); });
    window.addEventListener('focus', () => { if (drawer.open) refreshSummary(); });
    document.addEventListener('visibilitychange', () => { if (document.visibilityState === 'hidden') cancelSummary(); else if (drawer.open) refreshSummary(); });
  }
  const reset = (returnFocus = true) => {
    cancelSummary();
    window.clearTimeout(closeTimer);
    drawer.classList.remove('is-closing');
    document.documentElement.classList.remove('ynd-account-drawer-open');
    triggers().forEach(trigger => trigger.setAttribute('aria-expanded', 'false'));
    notify();
    if (returnFocus && opener?.isConnected && !document.querySelector('dialog[open]')) focus(opener);
  };
  const closeNow = () => {
    window.clearTimeout(closeTimer);
    if (drawer.open) drawer.close();
  };
  const close = () => {
    if (!drawer.open || drawer.classList.contains('is-closing')) return;
    cancelSummary();
    if (reducedMotion.matches) { closeNow(); return; }
    drawer.classList.add('is-closing');
    closeTimer = window.setTimeout(closeNow, 230);
  };
  drawer.addEventListener('animationend', event => {
    if (event.target === drawer && event.animationName === 'ynd-account-drawer-out') closeNow();
  });
  drawer.addEventListener('cancel', event => { event.preventDefault(); close(); });
  drawer.addEventListener('close', () => { if (!drawer.open) reset(restoreFocus); });
  drawer.addEventListener('click', event => {
    if (event.target.closest?.('[data-close-account-drawer]')) close();
    if (event.target !== drawer) return;
    const box = drawer.getBoundingClientRect();
    if (event.clientX < box.left || event.clientX > box.right || event.clientY < box.top || event.clientY > box.bottom) close();
  });
  document.addEventListener('click', event => {
    const trigger = event.target.closest?.('[data-open-account-drawer]');
    if (!trigger || event.defaultPrevented || event.button !== 0 || event.ctrlKey || event.metaKey || event.altKey || event.shiftKey) return;
    if ((trigger.target && trigger.target !== '_self') || trigger.hasAttribute('download')) return;
    if (drawer.open && drawer.classList.contains('is-closing')) {
      window.clearTimeout(closeTimer);
      drawer.classList.remove('is-closing');
      focus(drawer.querySelector('[data-close-account-drawer]'));
      refreshSummary();
    }
    if (!drawer.open) {
      try { drawer.showModal(); } catch { return; }
      refreshSummary();
      opener = trigger;
      restoreFocus = true;
      drawer.classList.remove('is-closing');
      document.documentElement.classList.add('ynd-account-drawer-open');
      triggers().forEach(element => element.setAttribute('aria-expanded', 'true'));
      notify();
      focus(drawer.querySelector('[data-close-account-drawer]'));
    }
    event.preventDefault();
  });
  document.addEventListener('keydown', event => {
    if (!drawer.open || event.defaultPrevented || event.key !== 'Tab' || event.ctrlKey || event.metaKey || event.altKey) return;
    const active = document.activeElement;
    if (active?.closest?.('dialog[open]') && !drawer.contains(active)) return;
    const items = [...drawer.querySelectorAll('a[href], button, [tabindex]')].filter(element => {
      if (element.tabIndex < 0 || element.matches(':disabled') || element.closest('[hidden], [inert], [aria-hidden="true"]') || !element.getClientRects().length) return false;
      const style = window.getComputedStyle(element);
      return style.display !== 'none' && style.visibility !== 'hidden' && style.visibility !== 'collapse';
    });
    const first = items[0], last = items[items.length - 1];
    if (!items.length || !items.includes(active) || (event.shiftKey ? active === first : active === last)) {
      event.preventDefault();
      focus((event.shiftKey ? last : first) || drawer);
    }
  });
  window.addEventListener('pagehide', () => { restoreFocus = false; closeNow(); reset(false); });
  window.addEventListener('pageshow', event => {
    if (event.persisted) { restoreFocus = false; closeNow(); reset(false); }
  });
  triggers().forEach(trigger => {
    trigger.setAttribute('aria-haspopup', 'dialog');
    trigger.setAttribute('aria-controls', drawer.id);
    trigger.setAttribute('aria-expanded', 'false');
  });
})();
